/** @jsxImportSource react */
import { useState } from 'react'
import { Alert, Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, TextField } from '@mui/material'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { approvalsAPI } from '../../api/crm'
import { useTenantKey } from '../../hooks/useCrmQueries'
import { getErrorMessage } from '../../utils/apiErrors'
import { useNotify } from '../common/NotificationProvider'
import { usePersonLabel } from '../records/PersonInput'

/** A save came back "needs approval": ask for it with a note. {@code ask}: { recordId, changes, details } */
export const approvalNeeded = (error) => error?.response?.status === 409 && error.response.data?.code === 'APPROVAL_REQUIRED'
export const approvalPending = (error) => error?.response?.status === 409 && error.response.data?.code === 'APPROVAL_PENDING'

const ApprovalAskDialog = ({ ask, onClose, onAsked }) => {
  const tenantId = useTenantKey()
  const queryClient = useQueryClient()
  const notify = useNotify()
  const personLabel = usePersonLabel()
  const [note, setNote] = useState('')
  const send = useMutation({
    mutationFn: () => approvalsAPI.ask(ask.recordId, ask.changes, note.trim() || null),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [tenantId, 'process'] })
      queryClient.invalidateQueries({ queryKey: [tenantId, 'approvals'] })
      notify('Approval requested')
      onAsked?.()
      onClose()
    },
  })
  const who = (ask.details?.approverIds || []).map((id) => personLabel(id)).join(', ')
  return (
    <Dialog open onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Needs approval</DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ mb: 2 }}>
          Moving to <strong>{ask.details?.toStage}</strong> needs approval ({ask.details?.ruleName}){who ? ` from ${who}` : ''}.
          The change is made as soon as it’s approved.
        </DialogContentText>
        {send.error && <Alert severity="error" sx={{ mb: 2 }}>{getErrorMessage(send.error)}</Alert>}
        <TextField label="Note for the approver" multiline minRows={2} fullWidth autoFocus value={note} onChange={(e) => setNote(e.target.value)}
          inputProps={{ maxLength: 2000 }} placeholder="Why this should go through" />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Not now</Button>
        <Button variant="contained" disabled={send.isPending} onClick={() => send.mutate()}>Ask for approval</Button>
      </DialogActions>
    </Dialog>
  )
}

export default ApprovalAskDialog
